import React, { ReactNode, createContext, useCallback, useContext } from 'react';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { useGuestAuthentication } from './hooks/use-guest-authentication';
import { IGuestDetails, IGuestSignInParams, IGuestUser } from './types';

export interface AuthGuestInterface {
  guestUser: IGuestUser | null;
  authenticated: boolean;
  anonymousAuthenticated: boolean;

  signIn(args: IGuestSignInParams): void;
  signInAnonymously(): void;
  signOut(): void;
  switchAnonymousUser(): void;
  updateGuestDetails(guestDetails: IGuestDetails): void;
  requestNewTokenAnonymously(): void;

  isAllowedToCheckout(): boolean;
  isAllowedToRequestPricing(isDelivery: boolean): boolean;

  hasOrderCommitted(): boolean;
  setOrderCommitted(): void;
}

const providerExceptionMessage = 'useAuthGuestContext must be used within an AuthGuestProvider';

const defaultAuthGuest: AuthGuestInterface = {
  guestUser: null,
  authenticated: false,
  anonymousAuthenticated: false,

  signIn: () => {
    throw new Error(providerExceptionMessage);
  },
  signInAnonymously: () => {
    throw new Error(providerExceptionMessage);
  },
  signOut: () => {
    throw new Error(providerExceptionMessage);
  },
  switchAnonymousUser: () => {
    throw new Error(providerExceptionMessage);
  },
  updateGuestDetails: () => {
    throw new Error(providerExceptionMessage);
  },
  requestNewTokenAnonymously: () => {
    throw new Error(providerExceptionMessage);
  },

  isAllowedToCheckout: () => {
    throw new Error(providerExceptionMessage);
  },
  isAllowedToRequestPricing: () => {
    throw new Error(providerExceptionMessage);
  },

  hasOrderCommitted: () => {
    throw new Error(providerExceptionMessage);
  },
  setOrderCommitted: () => {
    throw new Error(providerExceptionMessage);
  },
};

export const AuthGuestContext = createContext<AuthGuestInterface>(defaultAuthGuest);
export const useAuthGuestContext = () => useContext(AuthGuestContext);

export const AuthGuestProvider = ({ children }: { children: ReactNode }) => {
  const enableGuestCheckout = useFlag(LaunchDarklyFlag.ENABLE_GUEST_CHECKOUT);
  const {
    guestUser,
    guestDetails,
    authenticated,
    anonymousAuthenticated,
    signIn,
    signOut,
    signInAnonymously,
    updateGuestDetails,
    switchAnonymousUser,
    requestNewTokenAnonymously,
  } = useGuestAuthentication();

  const setOrderCommitted = useCallback(() => {
    updateGuestDetails({
      ...guestDetails,
      orderCount: 1,
    });
  }, [updateGuestDetails]);

  const hasOrderCommitted = useCallback(() => {
    return authenticated && guestDetails?.orderCount!! > 0;
  }, [authenticated, guestDetails]);

  const isAllowedToCheckout = useCallback(() => {
    return enableGuestCheckout; // TODO: TRX-1009 load data from sanity / store settings
  }, [enableGuestCheckout]);

  const isAllowedToRequestPricing = useCallback(
    (isDelivery: boolean) => {
      if (!isAllowedToCheckout()) {
        return false;
      }
      if (!authenticated) {
        return false;
      }
      if (guestDetails?.orderCount!! > 0) {
        return false;
      }
      if (anonymousAuthenticated) {
        return !isDelivery;
      }
      return true;
    },
    [isAllowedToCheckout, authenticated, anonymousAuthenticated, guestDetails]
  );

  const getContext = useCallback(() => {
    return {
      guestUser,
      authenticated,
      anonymousAuthenticated,
      signIn,
      signInAnonymously,
      signOut,
      updateGuestDetails,
      switchAnonymousUser,
      requestNewTokenAnonymously,
      isAllowedToCheckout,
      isAllowedToRequestPricing,
      hasOrderCommitted,
      setOrderCommitted,
    };
  }, [
    guestUser,
    authenticated,
    anonymousAuthenticated,
    signIn,
    signInAnonymously,
    signOut,
    updateGuestDetails,
    switchAnonymousUser,
    requestNewTokenAnonymously,
    isAllowedToCheckout,
    isAllowedToRequestPricing,
    hasOrderCommitted,
    setOrderCommitted,
  ]);

  return <AuthGuestContext.Provider value={getContext()}>{children}</AuthGuestContext.Provider>;
};
